'use client';

import { useTranslation } from 'react-i18next';

import { useTenantBranding } from '~/(tenant)/_components/tenant-branding-provider';
import {
  DEFAULT_NATIVE_DESIGN,
  type NativeDesignConfig,
  parseNativeDesignConfig,
} from '~/(tenant)/_lib/schema/native-design.schema';

export interface NativeTokens {
  // Surfaces
  background: string;
  surface: string;
  // Ink
  foreground: string;
  muted: string;
  // Structure
  line: string;
  border: string;
  // Brand accent — used by filled CTA backgrounds so the tenant's primary
  // color shows through on actions even when they haven't set a custom app
  // text color. Falls back to `foreground` when no primary is set or when
  // the tenant explicitly customised app_text_color.
  accent: string;
  // Geometry
  radius: number;
  radiusLg: number;
  // Typography families
  fontDisplay: string;
  fontSans: string;
  fontMono: string;
  // Density (drives spacing scale used by primitives)
  density: 'compact' | 'regular' | 'spacious';
}

const FONT_PRESETS = {
  system: {
    display: `-apple-system, BlinkMacSystemFont, system-ui, sans-serif`,
    sans: `-apple-system, BlinkMacSystemFont, system-ui, sans-serif`,
    mono: `ui-monospace, SFMono-Regular, monospace`,
  },
  serif: {
    display: `'Cormorant Garamond', 'Times New Roman', serif`,
    sans: `'Inter', -apple-system, system-ui, sans-serif`,
    mono: `'JetBrains Mono', ui-monospace, monospace`,
  },
} as const;

function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace('#', '');
  if (cleaned.length !== 6) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const DEFAULT_TEXT = '#1C1C1C';

export function buildNativeTokens(input: {
  appBackgroundColor: string;
  appTextColor: string;
  /** Tenant's primary brand color — used as the accent fallback when the
   *  tenant has NOT customised app_text_color (i.e. ink is the default). */
  primaryColor?: string;
  design: NativeDesignConfig;
  language?: string;
}): NativeTokens {
  const fontFamily = input.design.fontFamily ?? 'system';
  const fonts = FONT_PRESETS[fontFamily];
  const isArabic = input.language?.toLowerCase().split('-')[0] === 'ar';
  const arabicFont = 'var(--font-sans-arabic), sans-serif';

  // If the tenant hasn't customised app_text_color, prefer their primary
  // brand color for filled CTA backgrounds. This mirrors how /book uses
  // `--primary` on its buttons — keeps brand identity visible without
  // disturbing the editorial off-black-on-white body.
  const usingDefaultInk =
    input.appTextColor.toUpperCase() === DEFAULT_TEXT.toUpperCase();
  const accent =
    usingDefaultInk && input.primaryColor
      ? input.primaryColor
      : input.appTextColor;

  return {
    background: input.appBackgroundColor,
    surface: input.appBackgroundColor,
    foreground: input.appTextColor,
    muted: hexToRgba(input.appTextColor, 0.68),
    line: hexToRgba(input.appTextColor, 0.08),
    border: hexToRgba(input.appTextColor, 0.16),
    accent,
    radius: input.design.radius ?? DEFAULT_NATIVE_DESIGN.radius,
    radiusLg: input.design.radiusLg ?? DEFAULT_NATIVE_DESIGN.radiusLg,
    fontDisplay: isArabic ? arabicFont : fonts.display,
    fontSans: isArabic ? arabicFont : fonts.sans,
    fontMono: isArabic ? arabicFont : fonts.mono,
    density: input.design.density ?? 'regular',
  };
}

/**
 * Returns the semantic token map used by every native section + primitive.
 *
 * Tokens are derived from `business_settings.app_background_color` and
 * `business_settings.app_text_color` (NOT primary_color/secondary_color —
 * those are reserved for the website). Geometry and typography come from
 * `theme_config.native_design` jsonb.
 *
 * Usage:
 *   const t = useNativeTokens();
 *   <div style={{ background: t.background, color: t.foreground }} />
 */
export function useNativeTokens(): NativeTokens {
  const branding = useTenantBranding();
  const { i18n } = useTranslation();

  return buildNativeTokens({
    appBackgroundColor: branding.appBackgroundColor ?? '#FFFFFF',
    appTextColor: branding.appTextColor ?? '#1C1C1C',
    primaryColor: branding.primaryColor,
    language: i18n.resolvedLanguage ?? i18n.language,
    // Design config flows through theme_config; for now we read defaults.
    // Phase 4 wires in the per-tenant override from business_settings.
    design: parseNativeDesignConfig(branding.nativeDesign),
  });
}

export { hexToRgba };
